import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCode,
    FaGlobe,
    FaLayerGroup,
    FaProjectDiagram,
    FaRobot,
    FaRocket,
    FaServer,
    FaShieldAlt
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChevronRight,
    TiFlash,
    TiTime
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Roadmap = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);
  const timelineLineRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Section entry animations
    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    // Timeline growing line animation
    if (timelineLineRef.current) {
      gsap.fromTo(
        timelineLineRef.current,
        { height: 0 },
        {
          height: "100%",
          ease: "none",
          scrollTrigger: {
            trigger: "#roadmap-timeline",
            start: "top 60%",
            end: "bottom 40%",
            scrub: 1,
          },
        }
      );
    }

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const stats = [
    { value: "24", label: "Key Milestones", icon: <FaProjectDiagram className="text-cyan-400" /> },
    { value: "Phase 2", label: "Currently Live", icon: <TiFlash className="text-orange-400" /> },
    { value: "150+", label: "Active Contributors", icon: <FaCode className="text-purple-400" /> },
    { value: "1M+", label: "Dev Commits", icon: <FaServer className="text-emerald-400" /> },
  ];

  const roadmapPhases = [
    {
      phase: "Phase 1: Genesis",
      period: "Q1-Q2 2024",
      title: "Foundation & Core Architecture",
      status: "COMPLETED",
      statusColor: "emerald",
      icon: <FaShieldAlt />,
      milestones: [
        "Whitepaper 1.0 release and technical audit",
        "Genesis Block generation and Zero-Core beta",
        "Initial seed funding and executive team formation",
        "Community foundation and ecosystem DAO setup"
      ]
    },
    {
      phase: "Phase 2: Launch & Integration",
      period: "Q3 2024 - Q1 2025",
      title: "The Ecosystem Awakening",
      status: "LIVE",
      statusColor: "orange",
      icon: <FaRocket />,
      milestones: [
        "Mainnet public launch and token generation (TGE)",
        "Zero-Dex decentralised exchange integration",
        "Asset tokenisation suite for real estate & art",
        "Hardware wallet support and multi-sig vault"
      ]
    },
    {
      phase: "Phase 3: Intelligence & Growth",
      period: "Q2-Q4 2025",
      title: "AI-Driven Expansion",
      status: "DEVELOPMENT",
      statusColor: "purple",
      icon: <FaRobot />,
      milestones: [
        "Predictive AI analytics for portfolio management",
        "Zero-Lend protocol with institutional liquidity",
        "Cross-chain bridge for Ethereum & Solana",
        "Global regulatory sandbox participation"
      ]
    },
    {
      phase: "Phase 4: Global Institutionalisation",
      period: "2026 & Beyond",
      title: "The Zero Standard",
      status: "PLANNING",
      statusColor: "cyan",
      icon: <FaGlobe />,
      milestones: [
        "Mass-scale institutional node deployment",
        "Central Bank Digital Currency (CBDC) bridge layer",
        "Dynamic sharding for 1M+ transactions per second",
        "Global blockchain sustainability certification"
      ]
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/roadmap-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="roadmapHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.2}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="The Architecture of the Future"
              containerClass="text-center text-blue-50 font-zentry relative z-10 px-4"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Strategic Vision · Milestone Execution · Continuous Progression
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-24 mx-auto max-w-7xl">
        
        {/* Intro & Stats */}
        <div className="grid lg:grid-cols-2 gap-16 mb-40 items-center" ref={(el) => (sectionRefs.current[0] = el)}>
          <div>
            <div className="flex items-center gap-2 text-cyan-400 font-bold uppercase tracking-[0.3em] text-xs mb-6">
              <span className="w-8 h-px bg-cyan-400"></span>
              Strategic Vision
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
              A Transparent Path to <span className="text-cyan-400 font-zentry italic">Market Leadership</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              Our roadmap is more than a list of features; it's a commitment to engineering 
              the most robust, scalable, and decentralised financial ecosystem in existence. 
              We operate in public, shipping code that defines the next decade of Web3.
            </p>
            <GlassButton
              onClick={() => navigate('/ico')}
              variant="cyan"
              className="px-8 py-4"
            >
              <div className="flex items-center gap-3">
                Participate in Genesis <TiArrowRight className="text-xl" />
              </div>
            </GlassButton>
          </div>
          
          <div className="grid grid-cols-2 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative overflow-hidden p-8 bg-white/5 border border-white/10 rounded-[2.5rem] group hover:border-cyan-500/30 transition-all duration-500"
              >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-5xl">
                  {stat.icon}
                </div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Vertical Timeline */}
        <div id="roadmap-timeline" className="relative mb-40" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="text-center mb-24">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Evolutionary Phases</h3>
            <p className="text-gray-300">Track our progress from conception to global institutional adoption.</p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Background Line */}
            <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-white/10">
              {/* Growing Line */}
              <div ref={timelineLineRef} className="absolute top-0 left-0 w-full bg-gradient-to-b from-emerald-500 via-orange-500 via-purple-500 to-cyan-500 origin-top shadow-[0_0_15px_rgba(34,211,238,0.5)]" />
            </div>

            <div className="space-y-32">
              {roadmapPhases.map((phase, index) => (
                <div key={index} className="relative group">
                  {/* Phase Dot */}
                  <div className={`absolute left-6 md:left-1/2 -translate-x-1/2 top-2 w-5 h-5 rounded-full bg-black border-2 border-${phase.statusColor}-500 z-20 transition-transform group-hover:scale-150 shadow-[0_0_10px_rgba(0,0,0,1)]`}>
                    <div className={`absolute inset-0.5 rounded-full bg-${phase.statusColor}-500/30 animate-pulse`} />
                  </div>
                  
                  <div className={`flex flex-col md:flex-row items-start gap-12 md:gap-0 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Content Card */}
                    <div className="w-full md:w-[45%] pl-16 md:pl-0">
                      <motion.div
                        whileHover={{ y: -5 }}
                        className="relative p-8 md:p-10 bg-black/60 backdrop-blur-xl border border-white/10 rounded-[3rem] overflow-hidden group transition-all duration-500 shadow-2xl"
                      >
                        {/* Status Ribbon */}
                        <div className={`absolute top-0 right-10 px-4 py-1 rounded-b-xl bg-${phase.statusColor}-500/10 border-x border-b border-${phase.statusColor}-500/20 text-${phase.statusColor}-400 text-[10px] font-bold tracking-widest`}>
                          {phase.status}
                        </div>

                        {/* Icon & Period */}
                        <div className="flex items-center gap-6 mb-8">
                          <div className={`w-14 h-14 rounded-2xl bg-${phase.statusColor}-500/10 flex items-center justify-center text-3xl text-${phase.statusColor}-400 border border-white/5`}>
                            {phase.icon}
                          </div>
                          <div>
                            <div className="text-cyan-400 text-xs font-bold uppercase tracking-widest mb-1">{phase.phase}</div>
                            <div className="text-gray-500 text-xs flex items-center gap-1.5 font-medium italic"><TiTime /> {phase.period}</div>
                          </div>
                        </div>

                        <h4 className="text-2xl font-bold text-white mb-6 leading-tight group-hover:text-cyan-400 transition-colors">{phase.title}</h4>
                        
                        <ul className="space-y-4">
                          {phase.milestones.map((m, i) => (
                            <li key={i} className="flex gap-4 text-sm text-gray-300 leading-relaxed group/item">
                              <span className={`w-1.5 h-1.5 rounded-full bg-${phase.statusColor}-500 mt-2 shrink-0 group-hover/item:scale-150 transition-transform`} />
                              {m}
                            </li>
                          ))}
                        </ul>

                        {/* Interactive Gradient Glow */}
                        <div className={`absolute -bottom-24 -right-24 w-48 h-48 bg-gradient-radial from-${phase.statusColor}-500/10 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                      </motion.div>
                    </div>
                    
                    {/* Empty Space for Desktop Alternating */}
                    <div className="hidden md:block w-[10%] shrink-0" />
                    <div className="hidden md:block w-[45%]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-transparent" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-2xl" />
          
          <div className="relative z-10 py-24 px-8 text-center max-w-4xl mx-auto">
            <div className="w-20 h-20 rounded-3xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-4xl text-cyan-400 mx-auto mb-10">
              <FaLayerGroup />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Contribute to the <span className="font-zentry italic text-cyan-400">Open Protocol</span>
            </h3>
            <p className="text-gray-300 mb-12 text-lg leading-relaxed">
              Global innovation is a collaborative effort. Whether you're a developer looking to integrate 
              with Zero-Core or a partner seeking to launch assets on our infrastructure, 
              the future is waiting for your contribution.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <GlassButton
                onClick={() => window.open('https://github.com/zero-blockchain', '_blank')}
                variant="cyan"
                className="px-10 py-5"
              >
                <div className="flex items-center justify-center gap-3">
                  <FaCode className="text-xl" /> Build on Zero
                </div>
              </GlassButton>
              
              <GlassButton
                onClick={() => navigate('/support')}
                variant="gray"
                className="px-10 py-5"
              >
                <div className="flex items-center justify-center gap-3">
                  Partner with Us <TiChevronRight className="text-xl" />
                </div>
              </GlassButton>
            </div>
            
            {/* Trust Badges */}
            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="flex items-center gap-2 font-bold tracking-widest text-xs"><FaShieldAlt /> AUDITED BY QUANTSTAMP</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-xs"><FaGlobe /> ISO 27001 COMPLIANT</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-xs"><TiFlash /> 1M+ TPS CAPACITY</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Roadmap;
